import { useCallback } from "react";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type {
  BulkResolveSurveysRowResult,
  BulkResolveSurveysRowStatus,
} from "@event-planner/shared";
import {
  surveyImportSpec,
  type ParsedSurveyImportRow,
} from "@event-planner/shared/imports";
import {
  ResolveAndImportDialog,
  type ProposalRow,
} from "../../components/imports/ResolveAndImportDialog";
import { type BulkImportResultBucket } from "../../components/BulkImportResultTable";
import {
  bulkCreateSurveys,
  bulkResolveSurveys,
} from "../../services/surveyService";
import { dateTimeLocalToTimestamp } from "../../utils/timestamps";

interface ExcelSurveyImportDialogProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  /** IANA timezone - local datetime strings get converted to UTC against this. */
  timezone: string;
  /** Fires after a non-zero import run completes so the parent can refetch. */
  onImported?: () => void;
}

type SurveyProposal = ProposalRow<
  ParsedSurveyImportRow,
  BulkResolveSurveysRowResult
>;

type WriteResult = Awaited<ReturnType<typeof bulkCreateSurveys>>;

export function defaultIncludeForSurveyStatus(
  status: BulkResolveSurveysRowStatus | "invalid",
): { include: boolean; locked: boolean } {
  switch (status) {
    case "new":
      return { include: true, locked: false };
    case "unknown_session":
    case "invalid_options":
    case "invalid_schedule":
    case "failed":
    case "invalid":
      return { include: false, locked: true };
  }
}

/**
 * Bulk Surveys import (ADR-054). Wraps the shared `ResolveAndImportDialog`
 * shell with survey-specific resolve + write callables. Each row's
 * optional session-title reference is resolved server-side; the dialog
 * surfaces the matched session (or the `unknown_session` failure)
 * before any write.
 *
 * When `sendNotification=true`, the bulk-create callable writes a
 * companion notification doc in the same batch as the survey, linking
 * survey.notificationId to it (matches the admin compose-form flow).
 */
export function ExcelSurveyImportDialog({
  open,
  onClose,
  eventId,
  timezone,
  onImported,
}: ExcelSurveyImportDialogProps) {
  const buildCallableRow = useCallback(
    (r: ParsedSurveyImportRow) => {
      let scheduledAt: string | null = null;
      if (r.scheduleMode === "scheduled" && r.scheduledAtLocal) {
        scheduledAt = dateTimeLocalToTimestamp(r.scheduledAtLocal, timezone)
          .toDate()
          .toISOString();
      }
      return {
        question: r.question,
        type: r.type,
        options: r.options,
        session: r.session,
        scheduleMode: r.scheduleMode,
        scheduledAt,
        sendNotification: r.sendNotification,
        notificationTitle: r.notificationTitle,
        notificationBody: r.notificationBody,
        ratingAxisLabel: r.ratingAxisLabel,
        ratingScaleMin: r.ratingScaleMin,
        ratingScaleMax: r.ratingScaleMax,
        optionAxisLabel: r.optionAxisLabel,
        responseLabel: r.responseLabel,
        chartSubtitle: r.chartSubtitle,
      };
    },
    [timezone],
  );

  const resolveRows = useCallback(
    async (rows: ParsedSurveyImportRow[]) => {
      const response = await bulkResolveSurveys({
        eventId,
        rows: rows.map(buildCallableRow),
      });
      return response.results;
    },
    [eventId, buildCallableRow],
  );

  const writeRows = useCallback(
    async (rows: ParsedSurveyImportRow[]) => {
      return bulkCreateSurveys({
        eventId,
        rows: rows.map(buildCallableRow),
      });
    },
    [eventId, buildCallableRow],
  );

  const renderProposalCard = useCallback(
    (proposal: SurveyProposal, onToggle: () => void) => (
      <ProposalCard proposal={proposal} onToggle={onToggle} />
    ),
    [],
  );

  return (
    <ResolveAndImportDialog<
      ParsedSurveyImportRow,
      BulkResolveSurveysRowResult,
      WriteResult
    >
      open={open}
      onClose={onClose}
      onImported={onImported}
      title="Import Surveys from Excel"
      entityNoun={{ singular: "Survey", plural: "Surveys" }}
      pickerHelpText={
        <>
          Upload an Excel file (.xlsx) with one row per survey. Required:{" "}
          <strong>question</strong>, <strong>type</strong>,{" "}
          <strong>scheduleMode</strong>, and <strong>sendNotification</strong>.
          Session-scoped surveys reference the session by title; event-wide
          surveys leave session blank.
        </>
      }
      testIdSlug="survey"
      spec={surveyImportSpec}
      templateFileName="survey-import-template.xlsx"
      sheetName="Surveys"
      resolveRows={resolveRows}
      writeRows={writeRows}
      defaultIncludeForResolution={(resolution) =>
        defaultIncludeForSurveyStatus(resolution?.status ?? "invalid")
      }
      renderProposalCard={renderProposalCard}
      buildResultBuckets={({ writeResult, includedProposals, skippedProposals }) =>
        buildSurveyBuckets(writeResult, includedProposals, skippedProposals)
      }
      importedAnythingFromResult={(result) => result.summary.created > 0}
    />
  );
}

function ProposalCard({
  proposal,
  onToggle,
}: {
  proposal: SurveyProposal;
  onToggle: () => void;
}) {
  const { resolution, parsed, include, includeLocked, lineNumber } = proposal;
  if (!resolution) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 1.5 }}
        data-testid={`proposal-row-${lineNumber}`}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Checkbox
            checked={false}
            disabled
            data-testid={`proposal-include-${lineNumber}`}
          />
          <Stack spacing={0.5} flex={1} minWidth={0}>
            <Typography variant="body2" fontWeight={500}>
              Row {lineNumber}:{" "}
              {parsed.data.question || <em>(missing question)</em>}
            </Typography>
            <Typography variant="caption" color="error.main">
              {parsed.message ?? "Format error"}
            </Typography>
          </Stack>
          <Chip size="small" label="Format error" color="error" />
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, opacity: includeLocked ? 0.85 : 1 }}
      data-testid={`proposal-row-${lineNumber}`}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Checkbox
          checked={include}
          disabled={includeLocked}
          onChange={onToggle}
          data-testid={`proposal-include-${lineNumber}`}
        />
        <Stack spacing={0.5} flex={1} minWidth={0}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" fontWeight={500}>
              Row {lineNumber}: {resolution.typedQuestion}
            </Typography>
            <StatusChip status={resolution.status} />
          </Stack>
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            <Chip size="small" label={parsed.data.type} variant="outlined" />
            {parsed.data.type === "single_choice" && (
              <Chip
                size="small"
                label={`${parsed.data.options.length} options`}
                variant="outlined"
              />
            )}
            {resolution.proposal?.sessionTitle ? (
              <Chip
                size="small"
                label={`Session: ${resolution.proposal.sessionTitle}`}
                variant="outlined"
              />
            ) : (
              <Chip size="small" label="Event-wide" variant="outlined" />
            )}
            <Chip
              size="small"
              label={
                parsed.data.scheduleMode === "now"
                  ? "Open now"
                  : `Scheduled: ${parsed.data.scheduledAtLocal ?? ""}`
              }
              variant="outlined"
            />
            {parsed.data.sendNotification && (
              <Chip
                size="small"
                label="With announcement push"
                color="info"
                variant="outlined"
              />
            )}
          </Stack>
          {resolution.message && (
            <Typography variant="caption" color="warning.main">
              {resolution.message}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

function StatusChip({ status }: { status: BulkResolveSurveysRowStatus }) {
  switch (status) {
    case "new":
      return <Chip size="small" label="New, ready" color="success" />;
    case "unknown_session":
      return <Chip size="small" label="Unknown session" color="warning" />;
    case "invalid_options":
      return <Chip size="small" label="Invalid options" color="warning" />;
    case "invalid_schedule":
      return <Chip size="small" label="Invalid schedule" color="warning" />;
    case "failed":
      return <Chip size="small" label="Failed" color="error" />;
  }
}

interface ResultRow {
  lineNumber: number;
  question: string;
  status:
    | "success"
    | "unknown_session"
    | "invalid_options"
    | "invalid_schedule"
    | "failed"
    | "skipped"
    | "invalid";
  message: string | null;
}

function buildSurveyBuckets(
  writeResult: WriteResult | null,
  includedProposals: SurveyProposal[],
  skippedProposals: SurveyProposal[],
): BulkImportResultBucket[] {
  const writeRows: ResultRow[] = includedProposals.map((p, i) => {
    const r = writeResult?.results[i];
    if (!r) {
      return {
        lineNumber: p.lineNumber,
        question: p.parsed.data.question,
        status: "failed",
        message: "Skipped at write phase",
      };
    }
    const status: ResultRow["status"] =
      r.status === "created"
        ? "success"
        : r.status === "unknown_session"
          ? "unknown_session"
          : r.status === "invalid_options"
            ? "invalid_options"
            : r.status === "invalid_schedule"
              ? "invalid_schedule"
              : "failed";
    return {
      lineNumber: p.lineNumber,
      question: p.parsed.data.question,
      status,
      message: r.message ?? null,
    };
  });

  const skipRows: ResultRow[] = skippedProposals.map((p) => {
    if (!p.resolution) {
      return {
        lineNumber: p.lineNumber,
        question: p.parsed.data.question || "(no question)",
        status: "invalid",
        message: p.parsed.message ?? "Format error",
      };
    }
    return {
      lineNumber: p.lineNumber,
      question: p.resolution.typedQuestion,
      status: "skipped",
      message: p.resolution.message ?? null,
    };
  });

  const all = [...writeRows, ...skipRows].sort(
    (a, b) => a.lineNumber - b.lineNumber,
  );
  return [
    {
      id: "success",
      label: "Successfully Added",
      color: "success",
      rows: all
        .filter((r) => r.status === "success")
        .map((r) => ({
          id: `s-${r.lineNumber}`,
          primary: r.question,
          secondary: `Row ${r.lineNumber}`,
        })),
    },
    {
      id: "unknown_session",
      label: "Unknown Session",
      color: "warning",
      description:
        "The session title didn't match any session in this event. Create the session on the Agenda page first, then re-import.",
      rows: all
        .filter((r) => r.status === "unknown_session")
        .map((r) => ({
          id: `u-${r.lineNumber}`,
          primary: r.question,
          secondary: `Row ${r.lineNumber}: ${r.message ?? ""}`,
        })),
    },
    {
      id: "invalid_options",
      label: "Invalid Options",
      color: "warning",
      description: "single_choice surveys need at least 2 comma-separated options.",
      rows: all
        .filter((r) => r.status === "invalid_options")
        .map((r) => ({
          id: `o-${r.lineNumber}`,
          primary: r.question,
          secondary: `Row ${r.lineNumber}: ${r.message ?? ""}`,
        })),
    },
    {
      id: "invalid_schedule",
      label: "Invalid Schedule",
      color: "warning",
      description:
        "scheduledAt is required when scheduleMode is 'scheduled' (or 'later').",
      rows: all
        .filter((r) => r.status === "invalid_schedule")
        .map((r) => ({
          id: `i-${r.lineNumber}`,
          primary: r.question,
          secondary: `Row ${r.lineNumber}: ${r.message ?? ""}`,
        })),
    },
    {
      id: "skipped",
      label: "Skipped",
      color: "default",
      description: "Rows the organizer chose not to import.",
      rows: all
        .filter((r) => r.status === "skipped")
        .map((r) => ({
          id: `k-${r.lineNumber}`,
          primary: r.question,
          secondary: `Row ${r.lineNumber}${r.message ? `: ${r.message}` : ""}`,
        })),
    },
    {
      id: "failed",
      label: "Failed",
      color: "error",
      description: "Validation errors or callable rejections.",
      rows: all
        .filter((r) => r.status === "failed" || r.status === "invalid")
        .map((r) => ({
          id: `f-${r.lineNumber}`,
          primary: r.question,
          secondary: `Row ${r.lineNumber}: ${r.message ?? "unknown error"}`,
        })),
    },
  ];
}
